import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Bot,
  Copy,
  Image as ImageIcon,
  Megaphone,
  MessageCircle,
  MoreVertical,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useDeleteTemplate,
  useDuplicateTemplate,
  useTemplates,
  type Template,
} from "@/api/templates";
import { TemplateEditorSheet } from "@/components/editor/TemplateEditorSheet";

export function TemplatesPage() {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useTemplates();
  const deleteMut = useDeleteTemplate();
  const duplicateMut = useDuplicateTemplate();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);

  const list = templates ?? [];

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
    haptic.impact("medium");
  };

  const openEdit = (t: Template) => {
    setEditing(t);
    setEditorOpen(true);
    haptic.select();
  };

  const handleDelete = async (t: Template) => {
    if (!confirm(`Удалить шаблон «${t.name}»?`)) return;
    try {
      await deleteMut.mutateAsync(t.id);
      haptic.notify("success");
      toast.success("Шаблон удалён");
    } catch {
      haptic.notify("error");
      toast.error("Не удалось удалить");
    }
  };

  const handleDuplicate = async (t: Template) => {
    haptic.impact("light");
    try {
      await duplicateMut.mutateAsync(t.id);
      haptic.notify("success");
      toast.success("Копия шаблона создана");
    } catch {
      haptic.notify("error");
      toast.error("Не удалось дублировать");
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav">
      {/* Header */}
      <div className="flex items-center justify-between pt-2 pb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              navigate(-1);
              haptic.select();
            }}
            className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
            aria-label="Назад"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div>
            <div className="text-2xl font-semibold tracking-tight">Шаблоны</div>
            <div className="text-sm text-text-muted mt-0.5">
              {list.length === 0
                ? "Создайте первый шаблон"
                : `${list.length} ${pluralRu(list.length, "шаблон", "шаблона", "шаблонов")}`}
            </div>
          </div>
        </div>

        {list.length > 0 && (
          <button
            onClick={openCreate}
            className={cn(
              "size-11 rounded-full grid place-items-center",
              "bg-gradient-accent text-white",
              "shadow-[0_8px_24px_-8px_rgba(255,69,0,0.6)]",
              "active:scale-95 transition-transform",
            )}
            aria-label="Создать шаблон"
          >
            <Plus className="size-5" />
          </button>
        )}
      </div>

      <AnimatePresence mode="popLayout">
        {isLoading ? (
          <SkeletonList key="loading" />
        ) : list.length === 0 ? (
          <EmptyState key="empty" onAdd={openCreate} />
        ) : (
          <motion.div key="list" layout className="space-y-2.5">
            {list.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onOpen={() => openEdit(t)}
                onDuplicate={() => handleDuplicate(t)}
                onDelete={() => handleDelete(t)}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <TemplateEditorSheet
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        template={editing}
      />
    </div>
  );
}

// ============================================================
// Template card
// ============================================================

function TemplateCard({
  template,
  onOpen,
  onDuplicate,
  onDelete,
}: {
  template: Template;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isAuto = template.kind === "auto";
  const Icon = isAuto ? Bot : Megaphone;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
      <button
        onClick={onOpen}
        className={cn(
          "w-full text-left rounded-2xl p-4",
          "bg-white/[0.02] border border-white/[0.06]",
          "hover:bg-white/[0.04] hover:border-white/[0.1] transition-colors",
          "active:scale-[0.99]",
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "size-11 rounded-xl grid place-items-center shrink-0",
              isAuto ? "bg-info-soft text-info" : "bg-accent-soft text-accent",
            )}
          >
            <Icon className="size-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="text-[15px] font-medium truncate">{template.name}</div>
              {template.media_path && (
                <ImageIcon className="size-3 text-text-dim shrink-0" />
              )}
            </div>
            <div className="text-xs text-text-muted truncate">
              {previewText(template.text)}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span
                className={cn(
                  "text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-md",
                  isAuto
                    ? "bg-info/15 text-info"
                    : "bg-accent/15 text-accent",
                )}
              >
                {isAuto ? "Автоответ" : "Рассылка"}
              </span>
              {hasSpintax(template.text) && (
                <span className="text-[10px] text-text-muted flex items-center gap-0.5">
                  <Sparkles className="size-2.5" />
                  Spintax
                </span>
              )}
              {template.entities.length > 0 && (
                <span className="text-[10px] text-text-muted">
                  {template.entities.length} формат.
                </span>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
              haptic.select();
            }}
            className="size-8 grid place-items-center rounded-full hover:bg-white/[0.06] transition-colors -mt-1 -mr-1"
            aria-label="Меню"
          >
            <MoreVertical className="size-4 text-text-dim" />
          </button>
        </div>
      </button>

      <AnimatePresence>
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -4, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className={cn(
                "absolute top-12 right-3 z-50",
                "min-w-[160px] rounded-xl p-1",
                "glass-strong border border-white/[0.08]",
                "shadow-[0_20px_40px_-12px_rgba(0,0,0,0.7)]",
              )}
            >
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onDuplicate();
                }}
                className="w-full px-3 py-2 rounded-lg flex items-center gap-2 text-sm text-white hover:bg-white/[0.06] transition-colors"
              >
                <Copy className="size-3.5" />
                Дублировать
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
                className="w-full px-3 py-2 rounded-lg flex items-center gap-2 text-sm text-danger hover:bg-danger/10 transition-colors"
              >
                <Trash2 className="size-3.5" />
                Удалить
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function previewText(text: string): string {
  return text
    .replace(/\{([^|}]+)\|[^}]*\}/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 110);
}

function hasSpintax(text: string): boolean {
  return /\{[^{}]*\|[^{}]*\}/.test(text);
}

// ============================================================
// Empty state
// ============================================================

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative pt-6 pb-8"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-10 w-[80%] aspect-square rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,107,53,0.35) 0%, rgba(255,69,0,0.12) 38%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="relative flex flex-col items-center text-center px-6 space-y-5">
        <div className="relative size-32">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border border-dashed border-accent/30"
          />
          <motion.div
            animate={{ scale: [0.95, 1.04, 0.95] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-4 rounded-full border border-accent/15"
          />
          <div className="absolute inset-8 rounded-[24px] bg-gradient-accent grid place-items-center shadow-[0_20px_60px_-12px_rgba(255,69,0,0.6)]">
            <MessageCircle className="size-9 text-white" strokeWidth={2.2} />
          </div>
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-1 -right-1 size-7 rounded-xl bg-surface border border-white/[0.08] grid place-items-center"
          >
            <Sparkles className="size-3.5 text-accent" />
          </motion.div>
        </div>

        <div className="space-y-2 max-w-[300px]">
          <div className="text-[22px] font-semibold tracking-tight leading-tight">
            Создайте
            <br />
            <span className="text-gradient-accent">первый шаблон</span>
          </div>
          <div className="text-sm text-text-muted leading-relaxed">
            Текст с&nbsp;форматированием, премиум-эмодзи и&nbsp;Spintax — каждое сообщение
            будет уникальным.
          </div>
        </div>

        <Button size="lg" leftIcon={<Plus />} onClick={onAdd} className="mt-2 px-8">
          Создать шаблон
        </Button>
      </div>
    </motion.div>
  );
}

// ============================================================
// Skeleton + utils
// ============================================================

function SkeletonList() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-2.5"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[92px] rounded-2xl bg-white/[0.025] border border-white/[0.06] relative overflow-hidden"
        >
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
        </div>
      ))}
    </motion.div>
  );
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
